import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fly/core';
import { openFlyApp } from '@fly/shared';
import { FlySpinnerLoader } from '@fly/shared-ui';
import { Separator } from '@radix-ui/themes';

interface DesktopConnectionStatusProps {
  isConnected: boolean;
  startDownload: boolean;
  downloadCallback: () => void;
}

export const DesktopConnectionStatus: React.FC<
  DesktopConnectionStatusProps
> = ({ isConnected = false, startDownload = false, downloadCallback }) => {
  const handleDownload = () => downloadCallback();
  const handleLunch = () => openFlyApp('');
  const { t } = useTranslation();
  const statusKey = isConnected ? 'connected' : 'not_connected';
  const i18nKeyTitle = `app.dashboard.header.desktop_connection.${statusKey}.title`;
  const i18nKeySubTitle = `app.dashboard.header.desktop_connection.${statusKey}.subtitle`;

  const downloadContent = !startDownload ? (
    t('app.dashboard.header.desktop_connection.not_connected.cta', {
      defaultValue: 'Connect',
    })
  ) : (
    <FlySpinnerLoader size={12} />
  );

  return (
    <span
      className='connection-status flex h-full w-full flex-col text-custom-12'
      data-testid='desktop-connection-status'
    >
      <span className='flex flex-col px-fly-4 text-left'>
        <Text size='2' highContrast weight='bold'>
          {t(i18nKeyTitle, {
            defaultValue: isConnected
              ? 'Fly Desktop connected'
              : 'Fly Desktop not connected',
          })}
        </Text>
        <Text size='1' highContrast>
          {t(i18nKeySubTitle, {
            defaultValue: isConnected
              ? 'Local workspace is secure'
              : 'Configure package managers and enable Fly MCP in IDE.',
          })}
        </Text>
      </span>
      <Separator
        className='mt-fly-3 mb-fly-3'
        orientation='horizontal'
        size='4'
      />
      <span className='flex flex-col pr-fly-4 pl-fly-4'>
        {!isConnected ? (
          <Button
            variant='solid'
            className='w-full'
            size='1'
            onClick={handleDownload}
          >
            {downloadContent}
          </Button>
        ) : (
          <Button
            color='neutral'
            variant='ghost'
            size='1'
            onClick={handleLunch}
            iconEnd='arrow_top_right'
            className='justify-between text-(--fly-color-scheme-general-black)'
          >
            {t('app.dashboard.header.desktop_connection.connected.cta', {
              defaultValue: 'Fly App',
            })}
          </Button>
        )}
      </span>
    </span>
  );
};
